import { CurrencyPipe } from '@angular/common'

import { APP_INITIALIZER, NgModule } from '@angular/core'
import { MatDialogRef } from '@angular/material/dialog'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { EffectsModule } from '@ngrx/effects'
import { StoreModule } from '@ngrx/store'
import { translationChunksConfig, translationsEn } from '@spartacus/assets'
import { CartEntryAdapter } from '@spartacus/cart/base/core'
import {
  AuthConfigInitializer,
  BaseSiteService,
  ContextServiceMap,
  CurrencyService,
  I18nConfig,
  LanguageService,
  OccConfig,
  provideConfig,
  WindowRef
} from '@spartacus/core'
import {
  organizationTranslationChunksConfig,
  organizationTranslationsEn
} from '@spartacus/organization/administration/assets'
import { AdministrationRootModule } from '@spartacus/organization/administration/root'
import {
  orderApprovalTranslationChunksConfig,
  orderApprovalTranslationsEn
} from '@spartacus/organization/order-approval/assets'
import { OrderApprovalRootModule } from '@spartacus/organization/order-approval/root'
import { AppRoutingModule, PageSlotModule } from '@spartacus/storefront'
import { translationOverwrites } from '../../public/asaleocare.translate'
import { CustomRoutingModule } from './custom-routing/custom-routing.module'
import { FeaturesModule } from './features/Features.module'
import { CustomAuthConfigInitializer } from './features/user/custom-login/custom-auth-config-initializer.service'
import { CustomOccCartEntryAdapter } from './occ/adapters/cart/custom-occ-cart-entry.adapter'
import { AppConfigurationService } from './services/app-configuration.service'
import { CustomContextService } from './services/custom-context-service.service'
import { SpartacusModule } from './spartacus/spartacus.module'

export function initializeApp(
  appConfig: AppConfigurationService,
  baseSiteService: BaseSiteService,
  winRef: WindowRef,
  currencyService: CurrencyService
) {
  /** manually set basesite and currency on app init */
  const isAuCountry = winRef.document.URL.includes('/au')
  baseSiteService.setActive(isAuCountry ? 'asaleocare-au' : 'asaleocare-nz')
  currencyService.setActive(isAuCountry ? 'AUD' : 'NZD')

  return () => appConfig.load()
}

export function serviceMapFactory() {
  return {
    language: LanguageService,
    currency: CurrencyService,
    baseSite: BaseSiteService,
    custom: CustomContextService
  }
}

@NgModule({
  imports: [
    AppRoutingModule,
    StoreModule.forRoot({}),
    EffectsModule.forRoot([]),
    SpartacusModule,
    AdministrationRootModule,
    OrderApprovalRootModule,
    FeaturesModule,
    CustomRoutingModule,
    BrowserAnimationsModule,
    PageSlotModule
  ],
  providers: [
    provideConfig(<OccConfig>{
      backend: {
        occ: {
          //UNCOMMENT THIS BLOCK FOR LOCAL DEV WORK
          // baseUrl:
          //'https://localhost:9002', //IF COMMERCE RUNS IN YOUR LOCAL MACHINE
          // 'https://api-devconnect.asaleocare.com', //IF COMMERCE RUNS IN CLOUD
          // 'https://api-stgconnect.asaleocare.com', //IF COMMERCE RUNS IN CLOUD
          prefix: '/occ/v2/',
          endpoints: {
            product:
              'products/${productCode}?fields=DEFAULT,classifications,code,name,summary,price(formattedValue,DEFAULT),images(galleryIndex,FULL),averageRating,stock(DEFAULT),description,availableForPickup,url,numberOfReviews,manufacturer,categories(FULL),priceRange,multidimensional,configuratorType,configurable,tags,videoURL,productSize,uomMappings,unit,certificationLogos,environmentalInfo,newProduct,changedProduct,replacingPartNo,dangerousGoods,dangerousGoodsRegCode,dangerousGoodsUNPSN,dangerousGoodsPackingGrp,dangerousGoodsClass,dangerousGoodsShipping,hazchemCode,hazchemTemp,dgPackageDescription,dgPackageWeight,sdsIssueDate,sdsExpiryDate,hazardous,rawMaterial,rawMaterialCountry,unspscCode,waist,shelfLife,recyclable,wetnessIndicator,benefits,marketingText,brand,exWorkDays,minPurchaseApply,preDeliveryCheck,deliveryLeadTimeMappings,gstApplicable,sustainabilityLabels,packagingMaterial',
            setDeliveryAddress: 'orgUsers/${userId}/carts/${cartId}/addresses/delivery'
          }
        },
        media: {
          //UNCOMMENT THIS BLOCK FOR LOCAL DEV WORK
          // baseUrl:
          //'https://localhost:9002/backoffice', //IF COMMERCE RUNS IN YOUR LOCAL MACHINE
          // 'https://backoffice-devconnect.asaleocare.com' //IF COMMERCE RUNS IN CLOUD
          // 'https://backoffice-stgconnect.asaleocare.com' //IF COMMERCE RUNS IN CLOUiD
        }
      }
    }),
    provideConfig(<I18nConfig>{
      i18n: {
        resources: { en: translationsEn },
        chunks: translationChunksConfig,
        fallbackLang: 'en'
      }
    }),
    provideConfig({
      i18n: {
        resources: { en: organizationTranslationsEn },
        chunks: organizationTranslationChunksConfig
      }
    }),
    provideConfig({
      i18n: {
        resources: { en: orderApprovalTranslationsEn },
        chunks: orderApprovalTranslationChunksConfig
      }
    }),
    provideConfig({
      i18n: { resources: { en: translationsEn } }
    }),
    provideConfig({
      i18n: { resources: translationOverwrites }
    }),
    {
      provide: ContextServiceMap,
      useFactory: serviceMapFactory
    },
    {
      provide: CartEntryAdapter,
      useClass: CustomOccCartEntryAdapter
    },
    AppConfigurationService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AppConfigurationService, BaseSiteService, WindowRef, CurrencyService],
      multi: true
    },
    CurrencyPipe,
    {
      provide: MatDialogRef,
      useValue: {}
    },
    CustomAuthConfigInitializer,
    {
      provide: AuthConfigInitializer,
      useClass: CustomAuthConfigInitializer
    }
  ]
})
export class AppModule {}
